import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FaDownload, FaFile } from 'react-icons/fa';
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import FormSkeleton from '../../atoms/FormSkeleton';
import { dataProviderApi } from '../../../services/dataProviderApi';
import { getEventColor, isPartialExtraction } from './batchReportUtils';
import { NOT_COLLECTED_LABEL } from '../recommendation-engine/utils/partialCollection';
import ReportStatusBadge from './ReportStatusBadge';
import PresentationModal from '../../organisms/PresentationModal';

const InfoBlock: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{title}</h3>
        {children}
    </div>
);

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ReportPageView: React.FC = () => {
    const { id = '', document = '' } = useParams();
    const navigate = useNavigate();
    const decodedDoc = decodeURIComponent(document);

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        dataProviderApi.getReportPageView(id, decodedDoc)
            .then(setData)
            .catch(() => navigate('/network-error'))
            .finally(() => setIsLoading(false));
    }, [id, decodedDoc]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBack = () => navigate(-1);

    const [reprocessing, setReprocessing] = useState(false);
    const [payloadModal, setPayloadModal] = useState<{ open: boolean; content: any; s3Key: string }>({ open: false, content: null, s3Key: '' });

    const handleViewPayload = async (s3Key: string) => {
        setPayloadModal({ open: true, content: null, s3Key });
        try {
            const result = await dataProviderApi.getPayloadContent(id, decodedDoc, s3Key);
            setPayloadModal(prev => ({ ...prev, content: result.content }));
        } catch {
            setPayloadModal(prev => ({ ...prev, content: { error: 'Falha ao carregar payload' } }));
        }
    };

    const handleReprocess = async () => {
        setReprocessing(true);
        try {
            await dataProviderApi.reprocessReports(id, [decodedDoc]);
            setData((prev: any) => ({ ...prev, status: 'PROCESSING', canManualRetry: false }));
        } catch {
            alert('Erro ao reprocessar');
        } finally {
            setReprocessing(false);
        }
    };

    const handleDownloadExtraction = () => {
        dataProviderApi.downloadReportExtraction(id, decodedDoc).catch(() => alert('Erro ao baixar dados extraídos'));
    };

    return (
        <PageForm
            pageTitle="Detalhe do Relatório"
            apiVersion={data?.apiVersion ? `${data.apiName} - ${data.apiVersion}` : ''}
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Relatórios em lote', url: '/data-provider/batch-report/0/100/createdAt/DESC' },
                { label: 'Detalhe do lote', url: `/data-provider/batch-report/view/${id}/0/100/document/ASC` },
                { label: `Report ${decodedDoc}`, url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end gap-2">
                    <button className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded" onClick={handleBack}>
                        <IoMdArrowRoundBack />
                    </button>
                    {data?.canManualRetry && (
                        <button
                            onClick={handleReprocess}
                            disabled={reprocessing}
                            className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded text-sm px-4 py-2 disabled:opacity-50"
                        >
                            {reprocessing ? 'Reprocessando...' : 'Reprocessar'}
                        </button>
                    )}
                </div>
            }
        >
            <div className="p-2 space-y-4">
                {isLoading ? <FormSkeleton /> : data && (
                    <>
                        {/* Processing Data */}
                        <InfoBlock title="Dados do Processamento">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div><span className="text-gray-500">Documento:</span> <span className="font-medium">{data.document}</span></div>
                                <div><span className="text-gray-500">Tipo:</span> <span className="font-medium">{data.reportType || '-'}</span></div>
                                <div>
                                    <span className="text-gray-500">Status:</span>{' '}
                                    {data.canManualRetry ? (
                                        <span className="rounded px-2 py-0.5 text-xs font-bold text-white bg-orange-500">Retentar</span>
                                    ) : (
                                        <ReportStatusBadge status={data.status} />
                                    )}
                                </div>
                                <div><span className="text-gray-500">Criado em:</span> <span className="font-medium">{data.createdAt || '-'}</span></div>
                                <div><span className="text-gray-500">Atualizado em:</span> <span className="font-medium">{data.updatedAt || '-'}</span></div>
                                <div><span className="text-gray-500">Tentativas:</span> <span className="font-medium">{data.attempts || 0} / {data.maxAttempts || 3}</span></div>
                            </div>
                            {data.errorMessage && (
                                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                                    <p className="text-sm text-red-600"><strong>Erro:</strong> {data.errorMessage}</p>
                                </div>
                            )}
                        </InfoBlock>

                        {/* State Flow */}
                        <InfoBlock title="Fluxo de Estados">
                            <div className="flex gap-8">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {(data.events || []).map((event: any, idx: number) => (
                                            <Fragment key={idx}>
                                                <div className="flex flex-col items-center">
                                                    <div className={`w-6 h-6 rounded-full ${getEventColor(event.eventName)} flex items-center justify-center text-white text-xs font-bold`}>
                                                        {idx + 1}
                                                    </div>
                                                    <span className="text-xs text-gray-500 mt-1 max-w-[80px] text-center truncate">{event.eventName?.replace('REPORT_', '').replace('PENDING_REPORT_', '')}</span>
                                                </div>
                                                {idx < (data.events || []).length - 1 && <div className="w-8 h-0.5 bg-gray-300 mt-[-12px]" />}
                                            </Fragment>
                                        ))}
                                    </div>
                                </div>
                                <div className="w-px bg-gray-200" />
                                <div className="flex-1">
                                    <h4 className="text-xs font-semibold text-gray-500 mb-2">HISTÓRICO</h4>
                                    <div className="space-y-2">
                                        {(data.events || []).map((event: any, idx: number) => (
                                            <div key={idx} className="flex items-center gap-2 text-sm">
                                                <div className={`w-2 h-2 rounded-full ${getEventColor(event.eventName)}`} />
                                                <span className="text-gray-700">{event.eventName}</span>
                                                <span className="text-gray-400 text-xs ml-auto">{event.occurredAt || '-'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </InfoBlock>

                        {/* Extracted Data */}
                        {(data.status === 'COMPLETED' || isPartialExtraction(data.status, data.extractedPayload)) && data.extractedPayload && Object.keys(data.extractedPayload).length > 0 && (
                            <InfoBlock title={isPartialExtraction(data.status, data.extractedPayload) ? 'Dados Extraídos (parciais)' : 'Dados Extraídos'}>
                                {isPartialExtraction(data.status, data.extractedPayload) && (
                                    <p className="mb-2 text-sm text-amber-700 dark:text-amber-300">
                                        Processamento interrompido. Campos pendentes ({NOT_COLLECTED_LABEL}):{' '}
                                        {(data.extractedPayload.pending_fields || []).join(', ') || '-'}
                                    </p>
                                )}
                                <div className="flex justify-end mb-2">
                                    <button onClick={handleDownloadExtraction} className="text-sm text-green-600 hover:underline flex items-center gap-1">
                                        <FaDownload size={12} /> Download JSON
                                    </button>
                                </div>
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <JsonView data={data.extractedPayload} shouldExpandNode={allExpanded} style={defaultStyles} />
                                </div>
                            </InfoBlock>
                        )}

                        {/* Payload Files from Provider - grouped by attempt */}
                        {data.payloadFiles && Object.keys(data.payloadFiles).length > 0 && (
                            <InfoBlock title="Payloads do Fornecedor">
                                {Object.entries(data.payloadFiles).map(([attemptKey, files]: [string, any]) => {
                                    if (!Array.isArray(files)) return null;
                                    return (
                                    <div key={attemptKey} className="mb-4 last:mb-0">
                                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                                            {attemptKey === 'attempt1' ? 'Tentativa 1' : attemptKey === 'attempt2' ? 'Tentativa 2 (limite 50)' : 'Tentativa 3 (limite 20)'}
                                        </h4>
                                        <div className="grid grid-cols-3 gap-2">
                                            {files.map((file: any, idx: number) => (
                                                <div key={idx} className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <FaFile className="text-blue-500 shrink-0" size={12} />
                                                        <span className="text-sm font-medium text-gray-900 truncate">{file.filename}</span>
                                                        <span className="text-xs text-gray-400 shrink-0">{formatFileSize(file.size)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0 ml-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewPayload(file.s3Key)}
                                                            className="text-green-600 hover:text-green-800"
                                                            title="Visualizar"
                                                            aria-label={`Visualizar payload ${file.filename}`}
                                                        >
                                                            <FaFile size={12} />
                                                        </button>
                                                        <a href={file.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                                            <FaDownload size={12} />
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    );
                                })}
                            </InfoBlock>
                        )}

                        {/* Payload Content Modal */}
                        <PresentationModal
                            id="payload-content-modal"
                            show={payloadModal.open}
                            onClose={() => setPayloadModal({ open: false, content: null, s3Key: '' })}
                            header={`Payload: ${payloadModal.s3Key.split('/').pop() || ''}`}
                            height="500px"
                            isLoading={payloadModal.open && !payloadModal.content}
                            loadingMessage="Carregando payload..."
                        >
                            <div className="p-4 max-h-[500px] overflow-y-auto">
                                {payloadModal.content && (
                                    <JsonView data={payloadModal.content} shouldExpandNode={allExpanded} style={defaultStyles} />
                                )}
                            </div>
                        </PresentationModal>
                    </>
                )}
            </div>
        </PageForm>
    );
};

export default ReportPageView;
